// Used in learn-more.dialog.ts, assignable-preview-details.component.ts, assignable-expanded-singular.component.ts, workspace-details.component.ts, avenue-eval-student-video.component.ts

// Angular
import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DomSanitizer } from "@angular/platform-browser";

// Services/Types/Helpers
import { GetBookshelfUrlHelper } from "#helpers/get-bookshelf-url.helper";
import { LanguageHelper } from "#helpers/language.helper";
import { LocalStorage } from "#services/local-storage.service";
import { User as FidoUser } from "@carnegie-learning/data-access-fido";
import {
    Course,
    PPUser as User,
    PPAssignment
} from "cli-types";

// RXJS/Store
import * as courseStore from "#core/teacher-classes/store";
import * as fromStore from "#core/teacher-classes/store";
import * as loginStore from "#login/store";
import { Asset, Perform, assetActivityTypeIds, assetTypeIds, eBookActivity } from "@carnegie-learning/cure-types";
import { LTI_EDULASTIC_LAUNCH_TYPES } from "@carnegie-learning/data-access-angular-passport";
import { Store, select } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

export interface PreviewDialogData {
    userID: number;
    asset: Asset;
    compositeID: number;
    compositeType: string;
}

@Component({
    selector: "activity-preview",
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./activity-preview-dialog.component.html",
    styleUrls: ["./activity-preview-dialog.component.scss"],
    standalone: false
})
export class ActivityPreviewDialogComponent implements OnInit, OnDestroy {
    externalId: string;
    compositeID: number;
    teacher: User;
    fidoUserData: FidoUser;
    userID: number;
    readonly assetTypeIds = assetTypeIds;
    readonly assetActivityTypeIds = assetActivityTypeIds;
    compositeType: string;
    asset: Asset;
    embedUrl: any;
    currentSelectedCourse: Course;
    currentMathCourse: Course;
    readyCheckDestination = LTI_EDULASTIC_LAUNCH_TYPES.TEACHER_PREVIEW;

    private ngUnsubscribe = new Subject();

    constructor(
        private store_login: Store<loginStore.UserState>,
        private store_course: Store<courseStore.CoursesState>,
        public dialogRef: MatDialogRef<ActivityPreviewDialogComponent>,
        private languageHelper: LanguageHelper,
        private localStorage: LocalStorage,
        private getBookshelfUrl: GetBookshelfUrlHelper,
        private domSanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA) public data: PreviewDialogData
    ) {}

    ngOnInit() {
        this.asset = this.data.asset;
        this.compositeID = this.data.compositeID;
        this.compositeType = this.data.compositeType;
        this.userID = this.data.userID;
        this.fidoUserData = this.localStorage.get("fidoUserProfile");

        // used in Performance preview to retrieve max attempts when assignment data isn't available
        this.store_course
            .pipe(select(courseStore.getSelectedCourseObject))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((courseData: Course | null) => {
                if (courseData) {
                    this.currentSelectedCourse = courseData;
                } else {
                    this.store_course
                        .pipe(select(fromStore.getDropdownCourses))
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe((courses) => {
                            if (courses !== null) {
                                const foundMathCourse = courses.find((c) =>
                                    this.languageHelper.ifMathLanguage(
                                        c.subjectId
                                    )
                                );
                                if (foundMathCourse) {
                                    this.currentMathCourse = foundMathCourse;
                                }
                            }
                        });
                }
            });

        if (this.data.asset.assetTypeId === assetTypeIds.PERFORM) {
            this.externalId = (<Perform>this.data.asset).externalId;
        } else if (
            this.data.asset.assetTypeId === assetTypeIds.EBOOK_ACT ||
            this.data.asset.assetTypeId === assetTypeIds.EREADER
        ) {
            this.store_login
                .pipe(select(loginStore.getUserProfile))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((profile) => {
                    const url = this.getBookshelfUrl.activity(
                        <eBookActivity>this.asset,
                        profile,
                        null,
                        null
                    );
                    this.embedUrl =
                        this.domSanitizer.bypassSecurityTrustResourceUrl(url);
                });
        } else if (
            this.data.asset.assetTypeId === assetTypeIds.COG_TUTOR ||
            this.data.asset.assetTypeId === assetTypeIds.READYCHECK ||
            this.data.asset.assetTypeId === assetTypeIds.C2C
        ) {
            this.store_login
                .pipe(select(loginStore.getUserProfile))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((userProfile) => {
                    if (userProfile) {
                        this.teacher = userProfile;
                    } else {
                        this.teacher = this.localStorage.get("userProfile");
                    }
                });
        }
    }

    onSubmitMathWorkspace(event: PPAssignment) {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    onCancel() {
        this.dialogRef.close();
    }
}
